import { useCallback, useEffect, useRef } from 'react';
import MapGestureAction from '@alltrails/amplitude/enums/MapGestureAction';
import TapAnywhereMenuAction from '@alltrails/amplitude/enums/TapAnywhereMenuAction';
import TapAnywhereType from '@alltrails/amplitude/enums/TapAnywhereType';
import logMapPointTapped from '@alltrails/amplitude/events/logMapPointTapped';
import logTapAnywhereMenuTapped from '@alltrails/amplitude/events/logTapAnywhereMenuTapped';
import useValueRef from '@alltrails/core/hooks/useValueRef';
import { useAppSelector } from '../redux';
import { getMapBaseLayer } from '../utils/amplitudeHelpers';
import useMap from './useMap';

type UseTapAnywhereMenuAnalyticsParams = {
  /** Whether the menu is currently showing. Callers gate this on the tap anywhere feature flag. */
  isOpen: boolean;
  isAllTrailsFeature?: boolean;
  lat: number | undefined;
  lng: number | undefined;
  pointType: TapAnywhereType;
  /** The gesture that opened the menu. A right click or long press is a 'long press'; clicking a POI is a 'tap'. */
  source: MapGestureAction;
  sourceId?: string;
  tappedLayerId?: string;
};

/**
 * Reports the tap anywhere menu lifecycle: 'map point tapped' when the menu opens, and 'tap anywhere menu tapped'
 * for each action. Mirrors iOS: a menu that goes away without a terminal action reports 'close menu', and copying
 * coordinates is reported but, because it leaves the menu open, does not suppress a later 'close menu'.
 */
const useTapAnywhereMenuAnalytics = ({
  isOpen,
  isAllTrailsFeature,
  lat,
  lng,
  pointType,
  source,
  sourceId,
  tappedLayerId
}: UseTapAnywhereMenuAnalyticsParams) => {
  const map = useMap();
  const { baseStyleId, isEditing } = useAppSelector(state => ({
    baseStyleId: state.map.baseStyleId,
    isEditing: state.customRoute.isEditing
  }));
  // Read through a ref so a style change or zoom while the menu is open doesn't re-report the open
  const contextRef = useValueRef({ baseStyleId, isEditing: Boolean(isEditing), map });
  const didTakeActionRef = useRef(false);

  useEffect(() => {
    if (!isOpen || lat === undefined || lng === undefined) {
      return;
    }

    didTakeActionRef.current = false;
    const { baseStyleId: currentBaseStyleId, isEditing: currentIsEditing, map: currentMap } = contextRef.current;
    const mapLocation = currentIsEditing ? 'custom route' : 'explore';
    logMapPointTapped({
      is_alltrails_feature: isAllTrailsFeature,
      is_online: navigator.onLine,
      lat,
      lng,
      map_base_layer: getMapBaseLayer(currentBaseStyleId),
      map_location: mapLocation,
      point_type: pointType,
      source,
      source_id: sourceId,
      tapped_layer_id: tappedLayerId,
      zoom_level: currentMap?.getZoom()
    });

    return () => {
      if (!didTakeActionRef.current) {
        logTapAnywhereMenuTapped({
          action_taken: 'close menu',
          map_location: mapLocation,
          point_type: pointType,
          source_id: sourceId
        });
      }
    };
  }, [isOpen, isAllTrailsFeature, lat, lng, pointType, source, sourceId, tappedLayerId, contextRef]);

  const logMenuAction = useCallback(
    (action: Exclude<TapAnywhereMenuAction, 'close menu'>) => {
      if (!isOpen) {
        return;
      }
      if (action !== 'copy coordinates') {
        didTakeActionRef.current = true;
      }
      logTapAnywhereMenuTapped({
        action_taken: action,
        map_location: contextRef.current.isEditing ? 'custom route' : 'explore',
        point_type: pointType,
        source_id: sourceId
      });
    },
    [contextRef, isOpen, pointType, sourceId]
  );

  return { logMenuAction };
};

export default useTapAnywhereMenuAnalytics;
